import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Modal,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Divider,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox
} from '@material-ui/core';
import moment from 'moment';
import ws from 'utils/ws';
import { NumberFormat, Label } from 'components';
import { CHART_COLORS } from 'common/constants';
import theme from 'theme';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 960,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  actions: {
    justifyContent: 'flex-end'
  },
  content: {
    padding: 0
  },
  image: {
    width: '100%',
    objectFit: 'contain'
  }
}));

const ScanDetailsModal = props => {
  const { scan, onClose, className, ...rest } = props;
  const classes = useStyles();

  const [hiddenDetections, setHiddenDetections] = useState([]);

  useEffect(() => {
    setHiddenDetections([]);
  }, [scan]);

  const handleClose = () => {
    onClose();
  };

  if (!scan) {
    return null;
  }

  const scale = 0.5;

  const imageDownloadFile = scan.scanFiles
                ?.filter(f => f.type === 'SCAN_IMAGE' && f.key?.toLowerCase().endsWith('.png'))[0];

  const jsonDownloadFile = scan.scanFiles
                ?.filter(f => f.type === 'SCAN_PAYLOAD')[0];

  return (
    <Modal onClose={handleClose} open={scan !== null}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <CardHeader
          subheader={
            <>
              {scan.venue?.name}
              <br />
              {scan.device?.name}
              <br />
              Model: {scan.model}
              <br />
              {moment(
                scan.createdDateTime
              ).format('MMM Do YYYY, h:mm:ss a')}
            </>
          }
          />
        <Divider />

        <CardContent className={classes.content}>
          <Grid container>
            {scan.detections?.length > 0 && (
              <Grid item xs={12} lg={5}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Show</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scan.detections?.map((detection, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Label color={CHART_COLORS[i]}>
                            {detection.label}
                          </Label>
                        </TableCell>
                        <TableCell>
                          <NumberFormat
                            value={detection.confidence}
                            precision={2}
                            percentage={true}
                            suffix={'%'}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={hiddenDetections.indexOf(i) === -1}
                            onChange={e => {
                              if (e.target.checked) {
                                setHiddenDetections(prev =>
                                  prev.filter(key => key !== i)
                                );
                              } else {
                                setHiddenDetections(prev => [...prev, i]);
                              }
                            }}></Checkbox>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              lg={scan.detections?.length > 0 ? 7 : 12}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#F5F5F5',
                borderLeft: `1px solid #EEEEEE`,
                overflow: 'scroll'
              }}>
              {scan.scanFiles
                ?.filter(f => f.type === 'SCAN_IMAGE' && f.key?.toLowerCase().endsWith('.png'))
                .map(scanFile => (
                  <div
                    style={{
                      position: 'relative',
                      padding: theme.spacing(2)
                    }}
                    key={scanFile.key}>
                    <img
                      key={scanFile.key}
                      style={{
                        width: scanFile.width * scale,
                        height: scanFile.height * scale,
                        border: `1px solid #EEEEEE`
                      }}
                      src={ws.url(
                        '/aws/s3/object?bucket=' +
                          scanFile.bucket +
                          '&objectKey=' +
                          scanFile.key
                      )}
                    />
                    <svg
                      viewBox={`0 0 ${scanFile.width *
                        scale} ${scanFile.height * scale}`}
                      style={{
                        position: 'absolute',
                        top: theme.spacing(2),
                        left: theme.spacing(2),
                        width: scanFile.width * scale,
                        height: scanFile.height * scale,
                        pointerEvents: 'none'
                      }}>
                      {scan.detections?.map((detection, i) => (
                        <>
                          {hiddenDetections.indexOf(i) === -1 && (
                            <rect
                              key={i}
                              x={detection.left * scale}
                              y={detection.top * scale}
                              width={(detection.right - detection.left) * scale}
                              height={
                                (detection.bottom - detection.top) * scale
                              }
                              fill="none"
                              stroke={CHART_COLORS[i] || 'red'}
                              stroke-width="2"
                              rx="10"
                              ry="10"
                            />
                          )}
                        </>
                      ))}
                    </svg>
                  </div>
                ))}
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          {jsonDownloadFile && 
          <Button variant="contained" style={{marginRight: theme.spacing(1)}}
          onClick={() => window.location = ws.url(
              '/aws/s3/object?bucket=' + jsonDownloadFile.bucket + '&objectKey=' + jsonDownloadFile.key
            )}>
            Download JSON 
          </Button>
          }
          {imageDownloadFile && 
          <Button variant="contained" style={{marginRight: theme.spacing(1)}}
          onClick={() => window.location = ws.url(
              '/aws/s3/object?bucket=' + imageDownloadFile.bucket + '&objectKey=' + imageDownloadFile.key
            )}>
            Download Image 
          </Button>
          }
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

ScanDetailsModal.defaultProps = {
  open: false
};

export default ScanDetailsModal;
